import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { getFontFamily, useThemePalette } from '@/config/theme';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** Score that pops with a brief flash when it changes, plus - on the big
 *  (emphasized) score - a "GOAL!" celebration burst when the total increases. */
export function AnimatedScore({
  home,
  away,
  emphasized = false,
  celebrate = true,
}: {
  home: number;
  away: number;
  emphasized?: boolean;
  /** Show the little "GOAL!" chip on increase. Off where a bigger celebration
   *  already plays (the match header banner). */
  celebrate?: boolean;
}) {
  const palette = useThemePalette();
  const { locale, arabicNumerals, isRTL } = useLocale();
  const reducedMotion = useReducedMotion();
  const scale = useSharedValue(1);
  const flash = useSharedValue(0);
  const goal = useSharedValue(0);
  const previous = useRef(`${home}-${away}`);
  const prevTotal = useRef(home + away);

  useEffect(() => {
    const current = `${home}-${away}`;
    if (previous.current !== current) {
      previous.current = current;
      const total = home + away;
      const scored = total > prevTotal.current;
      prevTotal.current = total;
      if (!reducedMotion) {
        scale.value = withSequence(withSpring(1.18, { damping: 6 }), withSpring(1));
        flash.value = withSequence(
          withTiming(1, { duration: 120 }),
          withTiming(0, { duration: 700 }),
        );
        if (scored && emphasized && celebrate) {
          goal.value = withSequence(
            withSpring(1, { damping: 9 }),
            withDelay(1400, withTiming(0, { duration: 400 })),
          );
        }
      }
    }
  }, [home, away, reducedMotion, emphasized, celebrate, scale, flash, goal]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    color: flash.value > 0.5 ? palette.volt : palette.text,
  }));

  const goalStyle = useAnimatedStyle(() => ({
    opacity: goal.value,
    transform: [{ scale: 0.7 + goal.value * 0.3 }, { translateY: (1 - goal.value) * 8 }],
  }));

  return (
    <View>
      {emphasized && celebrate ? (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: -26,
              alignSelf: 'center',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              paddingHorizontal: 10,
              paddingVertical: 3,
              borderRadius: 999,
              backgroundColor: palette.volt,
            },
            goalStyle,
          ]}
        >
          <AppText weight="bold" style={{ fontSize: 12 }}>
            ⚽
          </AppText>
          <AppText weight="bold" style={{ color: palette.onVolt, fontSize: 12, letterSpacing: 1 }}>
            GOAL!
          </AppText>
        </Animated.View>
      ) : null}
      <Animated.Text
        numberOfLines={1}
        adjustsFontSizeToFit
        style={[
          {
            fontFamily: getFontFamily(locale, 'bold', true),
            fontSize: emphasized ? 38 : 17,
            fontVariant: ['tabular-nums'],
            textAlign: 'center',
          },
          animatedStyle,
        ]}
      >
        {/* Order matches the (RTL-mirrored) team layout; LRI/PDI lock the segment
            LTR so the bidi algorithm can't re-flip "0 - 2" back to "2 - 0". */}
        {`⁦${localizeDigits(isRTL ? `${away} - ${home}` : `${home} - ${away}`, arabicNumerals)}⁩`}
      </Animated.Text>
    </View>
  );
}
